import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Menu, X, Sun, Moon } from 'lucide-react';
import { companyInfo, navLinks } from '../data/mockData';
import { useTheme } from '../context/ThemeContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50">

      {/* Main Header */}
      <div className={`transition-all duration-300 ${
        isScrolled 
          ? isDark 
            ? 'bg-neutral-950 shadow-lg' 
            : 'bg-white shadow-lg'
          : isDark 
            ? 'bg-neutral-900' 
            : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img 
                src={companyInfo.logo} 
                alt="JPR Customs Logo" 
                className="h-16 md:h-20 w-auto object-contain"
              />
            </Link>

            {/* Right Side Actions - Desktop */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Email Button */}
              <a
                href={`mailto:${companyInfo.email}`}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all duration-300 border-2 border-lime-500 ${
                  isDark 
                    ? 'text-white hover:bg-lime-500 hover:text-black' 
                    : 'text-neutral-900 hover:bg-lime-500 hover:text-black'
                }`}
              >
                <Mail className="w-4 h-4" />
                Email Us
              </a>

              {/* Call Button */}
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-2 px-5 py-2.5 bg-lime-500 text-black font-semibold rounded-lg hover:bg-lime-400 transition-all duration-300"
              >
                <Phone className="w-4 h-4" />
                Call Us
              </a>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2.5 rounded-lg transition-all duration-300 ${
                  isDark 
                    ? 'bg-neutral-800 text-lime-400 hover:bg-neutral-700' 
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            </div>

            {/* Mobile Right Side */}
            <div className="flex lg:hidden items-center gap-2">
              {/* Theme Toggle - Mobile */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-all duration-300 ${
                  isDark 
                    ? 'bg-neutral-800 text-lime-400' 
                    : 'bg-neutral-100 text-neutral-700'
                }`}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark 
                    ? 'text-white hover:bg-neutral-800' 
                    : 'text-neutral-900 hover:bg-neutral-100'
                }`}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Navigation - Flat Style */}
        <nav className={`hidden lg:block border-t ${
          isDark ? 'border-neutral-800' : 'border-neutral-200'
        }`}>
          <div className="max-w-7xl mx-auto px-4">
            <ul className="flex items-center justify-center flex-wrap gap-1 py-2">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className={`block px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
                      location.pathname === link.path
                        ? 'bg-lime-500 text-black'
                        : isDark 
                          ? 'text-neutral-300 hover:bg-neutral-800 hover:text-lime-400' 
                          : 'text-neutral-700 hover:bg-neutral-100 hover:text-lime-600'
                    }`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden fixed inset-0 top-[76px] transition-all duration-300 ${
        isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
      } ${
        isDark ? 'bg-neutral-950' : 'bg-white'
      }`}>
        <nav className="max-w-7xl mx-auto px-4 py-6">
          <ul className="space-y-2">
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`block px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                    location.pathname === link.path
                      ? 'bg-lime-500 text-black'
                      : isDark 
                        ? 'text-neutral-300 hover:bg-neutral-800 hover:text-white' 
                        : 'text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900'
                  }`}
                >
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
          <div className={`mt-6 pt-6 border-t ${isDark ? 'border-neutral-800' : 'border-neutral-200'}`}>
            <div className="flex flex-col gap-3">
              <a
                href={`mailto:${companyInfo.email}`}
                className={`flex items-center justify-center gap-2 px-8 py-3 border-2 border-lime-500 font-semibold rounded-lg transition-all duration-300 ${
                  isDark ? 'text-white hover:bg-lime-500 hover:text-black' : 'text-neutral-900 hover:bg-lime-500 hover:text-black'
                }`}
              >
                <Mail className="w-5 h-5" />
                Email Us
              </a>
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-lime-500 text-black font-semibold rounded-lg hover:bg-lime-400 transition-all duration-300"
              >
                <Phone className="w-5 h-5" />
                Call Us: {companyInfo.phone}
              </a>
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
